import React, { useState, useEffect, useCallback, useRef } from 'react';
import { eventsApi, unitsApi, callsApi, employeesApi, assignmentsApi } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuSeparator,
} from '../components/ui/context-menu';
import { toast } from 'sonner';

// Status configuration with colors
const STATUS_CONFIG = {
  enroute: { label: 'ENROUTE', color: 'text-white', bgColor: 'bg-white/10' },
  on_scene: { label: 'ON SCENE', color: 'text-red-500', bgColor: 'bg-red-500/10' },
  transport: { label: 'TRANSPORTING', color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' },
  at_destination: { label: 'AT DESTINATION', color: 'text-orange-400', bgColor: 'bg-orange-400/10' },
  available: { label: 'AVAILABLE', color: 'text-green-500', bgColor: 'bg-green-500/10' },
  at_post: { label: 'AT POST', color: 'text-gray-400', bgColor: 'bg-gray-400/10' },
};

const STATUS_ORDER = ['enroute', 'on_scene', 'transport', 'at_destination', 'available', 'at_post'];

// Self-contained duration clock - manages its own timer, no parent re-renders
const DurationClock = React.memo(({ since }) => {
  const [display, setDisplay] = useState('00:00:00');
  
  useEffect(() => {
    if (!since) return;
    const calc = () => {
      const diffMs = Date.now() - new Date(since).getTime();
      const h = Math.floor(diffMs / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      const s = Math.floor((diffMs % 60000) / 1000);
      setDisplay(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [since]);
  
  return <>{display}</>;
});

// Self-contained header clock
const LiveClock = React.memo(() => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  
  const time = now.toLocaleTimeString('en-US', { hour12: false });
  const date = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  
  return (
    <>
      <p className="dos-text text-lg md:text-2xl">{time}</p>
      <p className="dos-text text-sm md:text-lg opacity-80">{date}</p>
    </>
  );
});

export default function StatusBoardPage() {
  const [events, setEvents] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [employees, setEmployees] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, unitsRes, callsRes, employeesRes] = await Promise.all([
        eventsApi.getActive(),
        unitsApi.getAll(),
        callsApi.getActive(),
        employeesApi.getAll()
      ]);
      
      setEvents(eventsRes.data);
      setAllUnits(unitsRes.data);
      setActiveCalls(callsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Track if context menu is open to prevent re-renders that close it
  const pendingRefresh = useRef(false);
  const contextMenuOpen = useRef(false);

  // WebSocket: queue refresh if context menu is open, otherwise refresh immediately
  useWebSocket(useCallback((msg) => {
    if (['unit_status_change', 'call_created', 'call_updated', 'assignment_created'].includes(msg.type)) {
      if (contextMenuOpen.current) {
        pendingRefresh.current = true;
      } else {
        fetchData();
      }
    }
  }, [fetchData]));

  // Flush pending refresh when context menu closes
  const handleContextMenuChange = useCallback((open) => {
    contextMenuOpen.current = open;
    if (!open && pendingRefresh.current) {
      pendingRefresh.current = false;
      fetchData();
    }
  }, [fetchData]);

  // Available units: only those with 'available' or 'at_post' status
  const availableUnits = allUnits.filter(u => ['available', 'at_post'].includes(u.status));
  
  // Assigned units: those actively on a call
  const assignedUnits = allUnits.filter(u => ['enroute', 'on_scene', 'transport', 'at_destination'].includes(u.status));

  const handleStatusChange = async (unitId, newStatus, transportDestination = null) => {
    try {
      await unitsApi.updateStatus(unitId, newStatus, transportDestination);
      const label = STATUS_CONFIG[newStatus]?.label || newStatus;
      const message = transportDestination ? `${label} / ${transportDestination}` : label;
      toast.success(`Status changed to ${message}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to change status');
      console.error(error);
    }
  };

  const handleAssignUnit = async (unitId, callId) => {
    try {
      await assignmentsApi.create(callId, unitId);
      toast.success('Unit assigned to call');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign unit');
      console.error(error);
    }
  };

  const handleDisposition = async (unitId, callId, disposition) => {
    try {
      await callsApi.setDisposition(callId, disposition, unitId);
      toast.success(`${disposition} - Unit moved to Available`);
      fetchData();
    } catch (error) {
      toast.error('Failed to set disposition');
      console.error(error);
    }
  };

  const handleCancelCall = async (callId) => {
    try {
      await callsApi.cancel(callId);
      toast.success('Call cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel call');
      console.error(error);
    }
  };

  // Pending calls: active calls with no units assigned
  const pendingCalls = activeCalls.filter(c => !c.assigned_units || c.assigned_units.length === 0);

  // Check if a pending call is overdue (> 2 minutes)
  const isPendingOverdue = (call) => {
    if (!call.created_at) return false;
    const created = new Date(call.created_at);
    const now = new Date();
    return (now - created) > 120000; // 2 minutes in ms
  };

  const getStatusDisplay = (unit) => {
    const status = unit.status;
    const config = STATUS_CONFIG[status] || { label: status?.toUpperCase() || 'UNKNOWN', color: 'text-white' };
    
    // Show destination for transport and at_destination
    if ((status === 'transport' || status === 'at_destination') && unit.transport_destination) {
      return {
        ...config,
        label: `${config.label} / ${unit.transport_destination.toUpperCase()}`
      };
    }
    
    return config;
  };

  // Get personnel names for a unit
  const getPersonnelNames = (unit) => {
    if (!unit.personnel || unit.personnel.length === 0) return 'N/A';
    return unit.personnel.map(empId => {
      const emp = employees.find(e => e.id === empId);
      return emp ? emp.name : 'Unknown';
    }).join(', ');
  };

  // Get available units for an event, grouped by type
  const getAvailableUnitsForEvent = (eventId) => {
    const eventAvailable = availableUnits.filter(u => u.event_id === eventId);
    return {
      mobile_response: eventAvailable.filter(u => u.unit_type === 'mobile_response'),
      supervisor: eventAvailable.filter(u => u.unit_type === 'supervisor'),
      shift_lead: eventAvailable.filter(u => u.unit_type === 'shift_lead'),
    };
  };

  // Group assigned units by event for Currently Assigned section
  const eventIds = events.map(e => e.id);
  const assignedByEvent = events.map(event => ({
    event,
    units: assignedUnits.filter(u => u.event_id === event.id)
  })).filter(({ units }) => units.length > 0);
  
  // Catch assigned units not matched to any loaded event
  const unassignedEventUnits = assignedUnits.filter(u => !eventIds.includes(u.event_id));

  // Group all units by event for Citywide Events
  const unitsByEvent = events.map(event => ({
    event,
    units: allUnits.filter(u => u.event_id === event.id)
  }));
  
  // Catch units not matched to any loaded event
  const unassignedEventAllUnits = allUnits.filter(u => !eventIds.includes(u.event_id));

  // Get medical areas for a unit's event
  const getMedicalAreas = (unit) => {
    const event = events.find(e => e.id === unit.event_id);
    return (event?.medical_areas || []).filter(a => a && a.trim());
  };

  // Unit context menu component
  const UnitContextMenu = ({ unit, children, showAssignOption = false }) => {
    const call = activeCalls.find(c => c.assigned_units?.includes(unit.id));
    const availableForEvent = showAssignOption ? getAvailableUnitsForEvent(unit.event_id) : null;
    const medicalAreas = getMedicalAreas(unit);
    const isOnCall = !!call;
    
    // If unit is NOT on a call, only allow Available <-> At Post
    const allowedStatuses = isOnCall ? STATUS_ORDER : ['available', 'at_post'];
    
    return (
      <ContextMenu onOpenChange={handleContextMenuChange}>
        <ContextMenuTrigger>{children}</ContextMenuTrigger>
        <ContextMenuContent className="bg-zinc-900 border-zinc-700 min-w-[200px]">
          <ContextMenuSub>
            <ContextMenuSubTrigger className="text-white">Change Status</ContextMenuSubTrigger>
            <ContextMenuSubContent className="bg-zinc-900 border-zinc-700">
              {allowedStatuses.map(status => {
                if (status === 'transport') {
                  if (medicalAreas.length === 0) {
                    return (
                      <ContextMenuItem 
                        key={status}
                        onClick={() => handleStatusChange(unit.id, status)}
                        className={`${STATUS_CONFIG[status].color} hover:bg-zinc-800`}
                      >
                        {STATUS_CONFIG[status].label}
                      </ContextMenuItem>
                    );
                  }
                  return (
                    <ContextMenuSub key={status}>
                      <ContextMenuSubTrigger className={`${STATUS_CONFIG[status].color} hover:bg-zinc-800`}>
                        {STATUS_CONFIG[status].label}
                      </ContextMenuSubTrigger>
                      <ContextMenuSubContent className="bg-zinc-900 border-zinc-700">
                        {medicalAreas.map(area => (
                          <ContextMenuItem 
                            key={area}
                            onClick={() => handleStatusChange(unit.id, status, area)}
                            className="text-yellow-400 hover:bg-zinc-800"
                          >
                            {area}
                          </ContextMenuItem>
                        ))}
                      </ContextMenuSubContent>
                    </ContextMenuSub>
                  );
                }
                return (
                  <ContextMenuItem 
                    key={status}
                    onClick={() => handleStatusChange(unit.id, status)}
                    className={`${STATUS_CONFIG[status].color} hover:bg-zinc-800`}
                  >
                    {STATUS_CONFIG[status].label}
                  </ContextMenuItem>
                );
              })}
            </ContextMenuSubContent>
          </ContextMenuSub>
          
          {/* Assign Additional Unit option for assigned units */}
          {showAssignOption && call && availableForEvent && (
            <>
              <ContextMenuSeparator className="bg-zinc-700" />
              <ContextMenuSub>
                <ContextMenuSubTrigger className="text-blue-400">Assign Additional Unit</ContextMenuSubTrigger>
                <ContextMenuSubContent className="bg-zinc-900 border-zinc-700 min-w-[180px]">
                  {availableForEvent.mobile_response.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs text-zinc-500 font-semibold">MOBILE RESPONSE</div>
                      {availableForEvent.mobile_response.map(u => (
                        <ContextMenuItem 
                          key={u.id}
                          onClick={() => handleAssignUnit(u.id, call.id)}
                          className="text-white hover:bg-zinc-800"
                        >
                          {u.unit_id}
                        </ContextMenuItem>
                      ))}
                    </>
                  )}
                  {availableForEvent.supervisor.length > 0 && (
                    <>
                      <ContextMenuSeparator className="bg-zinc-700" />
                      <div className="px-2 py-1 text-xs text-zinc-500 font-semibold">SUPERVISOR</div>
                      {availableForEvent.supervisor.map(u => (
                        <ContextMenuItem 
                          key={u.id}
                          onClick={() => handleAssignUnit(u.id, call.id)}
                          className="text-white hover:bg-zinc-800"
                        >
                          {u.unit_id}
                        </ContextMenuItem>
                      ))}
                    </>
                  )}
                  {availableForEvent.shift_lead.length > 0 && (
                    <>
                      <ContextMenuSeparator className="bg-zinc-700" />
                      <div className="px-2 py-1 text-xs text-zinc-500 font-semibold">SHIFT LEAD</div>
                      {availableForEvent.shift_lead.map(u => (
                        <ContextMenuItem 
                          key={u.id}
                          onClick={() => handleAssignUnit(u.id, call.id)}
                          className="text-white hover:bg-zinc-800"
                        >
                          {u.unit_id}
                        </ContextMenuItem>
                      ))}
                    </>
                  )}
                  {availableForEvent.mobile_response.length === 0 && 
                   availableForEvent.supervisor.length === 0 && 
                   availableForEvent.shift_lead.length === 0 && (
                    <div className="px-2 py-2 text-xs text-zinc-500">No available units</div>
                  )}
                </ContextMenuSubContent>
              </ContextMenuSub>
            </>
          )}
          
          {/* Disposition options - move unit to available */}
          {call && !['available', 'at_post'].includes(unit.status) && (
            <>
              <ContextMenuSeparator className="bg-zinc-700" />
              <ContextMenuItem 
                onClick={() => handleDisposition(unit.id, call.id, 'RMA/AMA')}
                className="text-cyan-400 hover:bg-zinc-800"
              >
                RMA/AMA
              </ContextMenuItem>
              <ContextMenuItem 
                onClick={() => handleDisposition(unit.id, call.id, 'UNFOUNDED')}
                className="text-cyan-400 hover:bg-zinc-800"
              >
                UNFOUNDED
              </ContextMenuItem>
              <ContextMenuItem 
                onClick={() => handleDisposition(unit.id, call.id, 'LEFT PRIOR')}
                className="text-cyan-400 hover:bg-zinc-800"
              >
                LEFT PRIOR
              </ContextMenuItem>
              <ContextMenuSeparator className="bg-zinc-700" />
              <ContextMenuItem 
                onClick={() => handleCancelCall(call.id)}
                className="text-red-500 hover:bg-red-500/10"
              >
                Cancel Call
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  return (
    <div className="min-h-screen bg-black p-4 crt-effect" data-testid="status-board-page">
      {/* Header */}
      <div className="dos-panel p-2 md:p-4 mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <h1 className="dos-text text-xl md:text-3xl tracking-widest dos-cursor">
              OSMC DISPATCH STATUS BOARD
            </h1>
            <p className="dos-text text-sm md:text-xl mt-1 opacity-80">
              MEDICAL STANDBY OPERATIONS CENTER
            </p>
          </div>
          <div className="text-left sm:text-right">
            <LiveClock />
          </div>
        </div>
      </div>

      {/* Main Grid - 2 columns top, 1 bottom */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Box 1: Available Units */}
        <div className="dos-panel p-4">
          <div className="border-b border-white pb-2 mb-4">
            <h2 className="dos-text text-2xl flex items-center gap-2">
              {'>'} AVAILABLE UNITS
              <span className="dos-text-warning ml-auto">[{availableUnits.length}]</span>
            </h2>
          </div>
          <ScrollArea className="h-[250px]">
            {availableUnits.length === 0 ? (
              <p className="dos-text opacity-60">NO UNITS AVAILABLE</p>
            ) : (
              <div className="space-y-1">
                {/* Header Row */}
                <div className="grid grid-cols-12 gap-2 border-b border-white/30 pb-1 mb-2">
                  <span className="dos-text opacity-60 col-span-2 text-sm">UNIT</span>
                  <span className="dos-text opacity-60 col-span-5 text-sm">PERSONNEL</span>
                  <span className="dos-text opacity-60 col-span-3 text-sm">STATUS</span>
                  <span className="dos-text opacity-60 col-span-2 text-sm">DURATION</span>
                </div>
                {availableUnits.map((unit) => {
                  const statusConfig = getStatusDisplay(unit);
                  return (
                    <UnitContextMenu key={unit.id} unit={unit}>
                      <div 
                        className="grid grid-cols-12 gap-2 py-1 cursor-pointer hover:bg-white/5"
                        data-testid={`status-available-${unit.id}`}
                      >
                        <span className="dos-text font-bold col-span-2">{unit.unit_id}</span>
                        <span className="dos-text opacity-80 col-span-5 truncate text-sm">{getPersonnelNames(unit)}</span>
                        <span className={`dos-text col-span-3 ${statusConfig.color} text-sm`}>
                          [{statusConfig.label}]
                        </span>
                        <span className="dos-text col-span-2 text-amber-400"><DurationClock since={unit.status_changed_at} /></span>
                      </div>
                    </UnitContextMenu>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Box 2: Citywide Events */}
        <div className="dos-panel p-4">
          <div className="border-b border-white pb-2 mb-4">
            <h2 className="dos-text text-2xl flex items-center gap-2">
              {'>'} CITYWIDE EVENTS
              <span className="dos-text-warning ml-auto">[{events.length}]</span>
            </h2>
          </div>
          <ScrollArea className="h-[250px]">
            {events.length === 0 ? (
              <p className="dos-text opacity-60">NO ACTIVE EVENTS</p>
            ) : (
              <div className="space-y-4">
                {unitsByEvent.map(({ event, units }) => (
                  <div key={event.id} className="border border-white/30 p-3" data-testid={`status-event-${event.id}`}>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="dos-text text-xl text-amber-400">{event.name.toUpperCase()}</h3>
                      <span className="dos-text opacity-60">{event.location}</span>
                    </div>
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-2 border-b border-white/20 pb-1 mb-2 pl-2">
                      <span className="dos-text opacity-60 col-span-2 text-xs">UNIT</span>
                      <span className="dos-text opacity-60 col-span-4 text-xs">PERSONNEL</span>
                      <span className="dos-text opacity-60 col-span-4 text-xs">STATUS</span>
                      <span className="dos-text opacity-60 col-span-2 text-xs">DURATION</span>
                    </div>
                    <div className="space-y-1">
                      {units.length === 0 ? (
                        <p className="dos-text opacity-60 text-sm pl-2">NO UNITS ASSIGNED TO EVENT</p>
                      ) : (
                        units.map(unit => {
                          const statusConfig = getStatusDisplay(unit);
                          const call = activeCalls.find(c => c.assigned_units?.includes(unit.id));
                          return (
                            <UnitContextMenu key={unit.id} unit={unit}>
                              <div className={`grid grid-cols-12 gap-2 pl-2 py-1 cursor-pointer hover:bg-white/5 ${call ? statusConfig.bgColor : ''}`}>
                                <span className="dos-text font-bold col-span-2">{unit.unit_id}</span>
                                <span className="dos-text opacity-80 col-span-4 truncate text-sm">{getPersonnelNames(unit)}</span>
                                <span className={`dos-text col-span-4 ${statusConfig.color} text-sm`}>
                                  [{statusConfig.label}]{call ? ` - ${call.call_type.toUpperCase()}` : ''}
                                </span>
                                <span className="dos-text col-span-2 text-amber-400"><DurationClock since={unit.status_changed_at} /></span>
                              </div>
                            </UnitContextMenu>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))}
                {/* Units not matched to any loaded event */}
                {unassignedEventAllUnits.length > 0 && (
                  <div className="border border-white/30 p-3">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="dos-text text-xl text-amber-400">OTHER UNITS</h3>
                    </div>
                    <div className="grid grid-cols-12 gap-2 border-b border-white/20 pb-1 mb-2 pl-2">
                      <span className="dos-text opacity-60 col-span-2 text-xs">UNIT</span>
                      <span className="dos-text opacity-60 col-span-4 text-xs">PERSONNEL</span>
                      <span className="dos-text opacity-60 col-span-4 text-xs">STATUS</span>
                      <span className="dos-text opacity-60 col-span-2 text-xs">DURATION</span>
                    </div>
                    <div className="space-y-1">
                      {unassignedEventAllUnits.map(unit => {
                        const statusConfig = getStatusDisplay(unit);
                        const call = activeCalls.find(c => c.assigned_units?.includes(unit.id));
                        return (
                          <UnitContextMenu key={unit.id} unit={unit}>
                            <div className={`grid grid-cols-12 gap-2 pl-2 py-1 cursor-pointer hover:bg-white/5 ${call ? statusConfig.bgColor : ''}`}>
                              <span className="dos-text font-bold col-span-2">{unit.unit_id}</span>
                              <span className="dos-text opacity-80 col-span-4 truncate text-sm">{getPersonnelNames(unit)}</span>
                              <span className={`dos-text col-span-4 ${statusConfig.color} text-sm`}>
                                [{statusConfig.label}]{call ? ` - ${call.call_type.toUpperCase()}` : ''}
                              </span>
                              <span className="dos-text col-span-2 text-amber-400"><DurationClock since={unit.status_changed_at} /></span>
                            </div>
                          </UnitContextMenu>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Box 3: Currently Assigned - Grouped by Event + Pending Calls */}
      <div className="dos-panel p-4">
        <div className="border-b border-white pb-2 mb-4">
          <h2 className="dos-text text-2xl flex items-center gap-2">
            {'>'} CURRENTLY ASSIGNED UNITS
            <span className="dos-text-warning ml-auto">[{assignedUnits.length} UNITS / {activeCalls.length} CALLS]</span>
          </h2>
        </div>
        <ScrollArea className="h-[500px]">
          {/* Assigned Units by Event - shown first */}
          {assignedByEvent.length === 0 && unassignedEventUnits.length === 0 && pendingCalls.length === 0 ? (
            <p className="dos-text opacity-60">NO ACTIVE ASSIGNMENTS</p>
          ) : (
            <div className="space-y-4">
              {assignedByEvent.map(({ event, units }) => (
                <div key={event.id} className="border border-white/20 p-3">
                  <div className="border-b border-white/20 pb-2 mb-2">
                    <h3 className="dos-text text-xl text-amber-400">{event.name.toUpperCase()}</h3>
                    <span className="dos-text text-sm opacity-60">{event.location}</span>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-white/20 pb-1 mb-2">
                    <span className="dos-text opacity-60 col-span-1 text-xs">UNIT</span>
                    <span className="dos-text opacity-60 col-span-3 text-xs">PERSONNEL</span>
                    <span className="dos-text opacity-60 col-span-2 text-xs">CALL TYPE</span>
                    <span className="dos-text opacity-60 col-span-4 text-xs">STATUS</span>
                    <span className="dos-text opacity-60 col-span-2 text-xs">DURATION</span>
                  </div>
                  <div className="space-y-1">
                    {units.map(unit => {
                      const statusConfig = getStatusDisplay(unit);
                      const call = activeCalls.find(c => c.assigned_units?.includes(unit.id));
                      return (
                        <UnitContextMenu key={unit.id} unit={unit} showAssignOption={true}>
                          <div 
                            className={`grid grid-cols-12 gap-2 py-1 px-2 cursor-pointer hover:bg-white/5 ${statusConfig.bgColor}`}
                            data-testid={`assigned-unit-${unit.id}`}
                          >
                            <span className="dos-text font-bold col-span-1">{unit.unit_id}</span>
                            <span className="dos-text opacity-80 col-span-3 truncate text-sm">{getPersonnelNames(unit)}</span>
                            <span className="dos-text col-span-2 text-amber-400 text-sm">
                              {call ? call.call_type.toUpperCase().substring(0, 12) : 'N/A'}
                            </span>
                            <span className={`dos-text col-span-4 ${statusConfig.color} text-sm`}>
                              [{statusConfig.label}]
                            </span>
                            <span className="dos-text col-span-2 text-amber-400"><DurationClock since={unit.status_changed_at} /></span>
                          </div>
                        </UnitContextMenu>
                      );
                    })}
                  </div>
                </div>
              ))}
              {unassignedEventUnits.length > 0 && (
                <div className="border border-white/20 p-3">
                  <div className="border-b border-white/20 pb-2 mb-2">
                    <h3 className="dos-text text-xl text-amber-400">OTHER ASSIGNED UNITS</h3>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-white/20 pb-1 mb-2">
                    <span className="dos-text opacity-60 col-span-1 text-xs">UNIT</span>
                    <span className="dos-text opacity-60 col-span-3 text-xs">PERSONNEL</span>
                    <span className="dos-text opacity-60 col-span-2 text-xs">CALL TYPE</span>
                    <span className="dos-text opacity-60 col-span-4 text-xs">STATUS</span>
                    <span className="dos-text opacity-60 col-span-2 text-xs">DURATION</span>
                  </div>
                  <div className="space-y-1">
                    {unassignedEventUnits.map(unit => {
                      const statusConfig = getStatusDisplay(unit);
                      const call = activeCalls.find(c => c.assigned_units?.includes(unit.id));
                      return (
                        <UnitContextMenu key={unit.id} unit={unit} showAssignOption={true}>
                          <div className={`grid grid-cols-12 gap-2 py-1 px-2 cursor-pointer hover:bg-white/5 ${statusConfig.bgColor}`}>
                            <span className="dos-text font-bold col-span-1">{unit.unit_id}</span>
                            <span className="dos-text opacity-80 col-span-3 truncate text-sm">{getPersonnelNames(unit)}</span>
                            <span className="dos-text col-span-2 text-amber-400 text-sm">
                              {call ? call.call_type.toUpperCase().substring(0, 12) : 'N/A'}
                            </span>
                            <span className={`dos-text col-span-4 ${statusConfig.color} text-sm`}>
                              [{statusConfig.label}]
                            </span>
                            <span className="dos-text col-span-2 text-amber-400"><DurationClock since={unit.status_changed_at} /></span>
                          </div>
                        </UnitContextMenu>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pending Calls (no units assigned) - shown after assigned units */}
          {pendingCalls.length > 0 && (
            <div className="border border-yellow-500/50 p-3 mb-4">
              <div className="border-b border-yellow-500/30 pb-2 mb-2">
                <h3 className="dos-text text-xl text-yellow-400">PENDING CALLS - NEEDS UNIT ASSIGNMENT</h3>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-white/20 pb-1 mb-2">
                <span className="dos-text opacity-60 col-span-3 text-xs">CALL TYPE</span>
                <span className="dos-text opacity-60 col-span-4 text-xs">LOCATION</span>
                <span className="dos-text opacity-60 col-span-3 text-xs">STATUS</span>
                <span className="dos-text opacity-60 col-span-2 text-xs">DURATION</span>
              </div>
              <div className="space-y-1">
                {pendingCalls.map(call => {
                  const overdue = isPendingOverdue(call);
                  return (
                    <ContextMenu key={call.id} onOpenChange={handleContextMenuChange}>
                      <ContextMenuTrigger>
                        <div 
                          className={`grid grid-cols-12 gap-2 py-1 px-2 cursor-pointer hover:bg-white/5 ${overdue ? 'pending-flash' : 'bg-yellow-400/10'}`}
                          data-testid={`pending-call-${call.id}`}
                        >
                          <span className="dos-text text-amber-400 font-bold col-span-3">{call.call_type?.toUpperCase()}</span>
                          <span className="dos-text opacity-80 col-span-4 truncate text-sm">{call.location}</span>
                          <span className="dos-text text-yellow-400 col-span-3 text-sm font-bold">[PENDING]</span>
                          <span className="dos-text col-span-2 text-amber-400"><DurationClock since={call.created_at} /></span>
                        </div>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="bg-zinc-900 border-zinc-700 min-w-[200px]">
                        <ContextMenuSub>
                          <ContextMenuSubTrigger className="text-blue-400">Assign Unit</ContextMenuSubTrigger>
                          <ContextMenuSubContent className="bg-zinc-900 border-zinc-700">
                            {availableUnits.length === 0 ? (
                              <div className="px-2 py-2 text-xs text-zinc-500">No available units</div>
                            ) : (
                              availableUnits.map(u => (
                                <ContextMenuItem 
                                  key={u.id}
                                  onClick={() => handleAssignUnit(u.id, call.id)}
                                  className="text-white hover:bg-zinc-800"
                                >
                                  {u.unit_id} ({getPersonnelNames(u)})
                                </ContextMenuItem>
                              ))
                            )}
                          </ContextMenuSubContent>
                        </ContextMenuSub>
                        <ContextMenuSeparator className="bg-zinc-700" />
                        <ContextMenuItem 
                          onClick={() => handleCancelCall(call.id)}
                          className="text-red-500 hover:bg-red-500/10"
                        >
                          Cancel Call
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  );
                })}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Footer */}
      <div className="dos-panel p-2 mt-4">
        <div className="flex justify-between items-center">
          <span className="dos-text text-sm opacity-60">
            SYSTEM STATUS: ONLINE | LIVE UPDATES | RIGHT-CLICK TO CHANGE STATUS
          </span>
          <span className="dos-text text-sm opacity-60">
            OSMC COMMUNICATION DIVISION v1.0
          </span>
        </div>
      </div>
    </div>
  );
}
